import { GitCompareArrows } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { LedgerDivergenceRow } from '@pf/contracts';
import { useLedgerDivergences } from '@/lib/api/ops-queries';
import { formatDateTime } from '@/lib/datetime';
import { ParticipantChip } from '@/components/domain/participant-chip';
import { WebScopeScopedNotice } from '@/components/domain/web-scope-select';
import { DataTable, type Column } from '@/components/layout/data-table';
import { PageHeader } from '@/components/layout/page';
import { Code } from '@/components/ui/primitives';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/ui/feedback';

/**
 * Ledger divergences a participant's own reconciliation reported (2026-08-27 observability pair,
 * `@pf/contracts` `observability.ts`). Read-only, and deliberately not on the money path: nothing
 * here is a PF transition, a claim, or a credit instruction — a website's own ledger already
 * detected each of these, and this page is only where a PF operator gets to see it too.
 *
 * `op` and `code` are the reporting website's *own* ledger vocabulary — PF defines no enum over
 * them (`LedgerDivergenceRowSchema`), so they render exactly as sent, in a monospace pill, rather
 * than through a translated label table the way every PF-owned status on this console is. That is
 * why this page has no `states.ts` entry the way a deposit or withdrawal status would: there is
 * nothing here for PF to classify.
 *
 * No pagination for v1 — the newest 200 rows (the endpoint's own ceiling) newest first, which is
 * the same "fetch the cap, no page controls" shape {@link useLedgerDivergences}'s dashboard sibling
 * uses for its stuck-count tile. A backlog past 200 genuinely recent divergences is itself the
 * incident; paging through it is not this screen's job.
 */
export function LedgerDivergencesPage(): ReactNode {
  const { t } = useTranslation();
  const divergences = useLedgerDivergences({ limit: 200 });

  const columns: Column<LedgerDivergenceRow>[] = [
    {
      id: 'op',
      header: t('ledgerDivergences.columns.op'),
      priority: 'primary',
      cell: (row) => <Code>{row.op}</Code>,
    },
    {
      id: 'code',
      header: t('ledgerDivergences.columns.code'),
      priority: 'primary',
      cell: (row) => <Code>{row.code}</Code>,
    },
    {
      id: 'lockReference',
      header: t('ledgerDivergences.columns.lockReference'),
      priority: 'secondary',
      cell: (row) => <span className="break-all font-mono text-xs">{row.lock_reference}</span>,
    },
    {
      id: 'occurredAt',
      header: t('ledgerDivergences.columns.occurredAt'),
      priority: 'secondary',
      cell: (row) => (
        <span className="whitespace-nowrap text-xs">{formatDateTime(row.occurred_at)}</span>
      ),
    },
    {
      id: 'website',
      header: t('ledgerDivergences.columns.website'),
      priority: 'secondary',
      cell: (row) => <ParticipantChip participant={row.participant_id} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('ledgerDivergences.title')}
        description={t('ledgerDivergences.description')}
      />
      <WebScopeScopedNotice />

      {divergences.isLoading ? <TableSkeleton /> : null}
      {divergences.isError ? (
        <ErrorState
          error={divergences.error}
          onRetry={() => {
            void divergences.refetch();
          }}
        />
      ) : null}
      {divergences.data !== undefined ? (
        <DataTable
          caption={t('ledgerDivergences.title')}
          columns={columns}
          rows={divergences.data.items}
          rowKey={(row) => row.id}
          empty={
            <EmptyState
              icon={<GitCompareArrows className="size-5" aria-hidden="true" />}
              title={t('ledgerDivergences.empty.title')}
              description={t('ledgerDivergences.empty.description')}
            />
          }
        />
      ) : null}
    </>
  );
}
